import React from 'react';
import { Box, Typography, Grid, Paper, Stack, Divider } from '@mui/material';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import GroupIcon from '@mui/icons-material/Group';
import TimelineIcon from '@mui/icons-material/Timeline';

const EmotionalImpactVisual = () => {
  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
        Psychological and Emotional Impact of Falls
      </Typography>
      
      <Grid container spacing={3}>
        {/* Fear of Falling */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
              border: '1px solid',
              borderColor: 'divider',
              borderLeft: '4px solid',
              borderLeftColor: 'error.main',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-5px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SentimentVeryDissatisfiedIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Fear of Falling
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Stack spacing={2}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  "Fear of falling is as disabling as the fall itself"
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                  Up to 70% of older adults who have fallen develop a fear of falling again, leading to:
                </Typography>
              </Box>
              
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'error.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                  Activity restriction and self-imposed limitations
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'error.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                  Decreased physical function due to reduced activity
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'error.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                  Social isolation and withdrawal from activities
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'error.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                  Increased risk of future falls due to deconditioning
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'error.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                  Decreased quality of life and independence
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        
        {/* Depression and Anxiety */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
              border: '1px solid',
              borderColor: 'divider',
              borderLeft: '4px solid',
              borderLeftColor: 'warning.main',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-5px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PsychologyIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Depression and Anxiety
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Falls can trigger or worsen mental health conditions:
                </Typography>
              </Box>
              
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'warning.main', borderRadius: '50%', display: 'inline-block', mr: 1.5, mt: 0.7 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Depression</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>25-30% of older adults develop depression after a fall</Typography>
                  </Box>
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'warning.main', borderRadius: '50%', display: 'inline-block', mr: 1.5, mt: 0.7 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Anxiety</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Persistent worry about falling again, even in safe situations</Typography>
                  </Box>
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'warning.main', borderRadius: '50%', display: 'inline-block', mr: 1.5, mt: 0.7 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>PTSD symptoms</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Flashbacks or intrusive thoughts about the fall</Typography>
                  </Box>
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'warning.main', borderRadius: '50%', display: 'inline-block', mr: 1.5, mt: 0.7 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Helplessness</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Feeling of loss of control over one's body and environment</Typography>
                  </Box>
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'flex-start' }}>
                  <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'warning.main', borderRadius: '50%', display: 'inline-block', mr: 1.5, mt: 0.7 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Catastrophic thinking</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Exaggerated perception of fall risk and consequences</Typography>
                  </Box>
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        
        {/* Loss of Independence */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
              border: '1px solid',
              borderColor: 'divider',
              borderTop: '4px solid',
              borderTopColor: 'info.main',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-5px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccessibilityNewIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Loss of Independence
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Falls often mark a transition toward increased dependence:
            </Typography>
            
            <Stack spacing={1.5}>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'info.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                40% of nursing home admissions follow a fall
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'info.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                Loss of ability to perform ADLs independently
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'info.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                Increased reliance on caregivers or assistive devices
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'info.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                Feelings of being a burden to family members
              </Typography>
            </Stack>
          </Paper>
        </Grid>
        
        {/* Social Impact */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
              border: '1px solid',
              borderColor: 'divider',
              borderTop: '4px solid',
              borderTopColor: 'success.main',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-5px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <GroupIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Social Impact
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Falls often lead to social consequences:
            </Typography>
            
            <Stack spacing={1.5}>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'success.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                Withdrawal from social activities and gatherings
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'success.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                Embarrassment about using assistive devices
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'success.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                Strained relationships with caregivers
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'success.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                Reduced participation in meaningful activities
              </Typography>
            </Stack>
          </Paper>
        </Grid>
        
        {/* Long-term Psychological Effects */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
              border: '1px solid',
              borderColor: 'divider',
              borderTop: '4px solid',
              borderTopColor: 'secondary.main',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-5px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TimelineIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Long-term Effects
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Psychological effects can persist long after physical recovery:
            </Typography>
            
            <Stack spacing={1.5}>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'secondary.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                Chronic anxiety about mobility and safety
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'secondary.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                Persistent fear even after physical rehabilitation
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'secondary.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                Decreased self-efficacy and confidence
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ width: 6, height: 6, bgcolor: 'secondary.main', borderRadius: '50%', display: 'inline-block', mr: 1.5 }} />
                Altered self-perception and identity
              </Typography>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed', borderColor: 'primary.main' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
          Caregiver Insight: The Emotional Impact Cycle
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Psychological impacts often create a negative cycle: fear leads to activity restriction, which causes physical deconditioning, 
          which increases actual fall risk, reinforcing the fear. Breaking this cycle requires addressing both physical AND psychological 
          aspects of fall prevention simultaneously.
        </Typography>
      </Box>
    </Box>
  );
};

export default EmotionalImpactVisual;
